import React from 'react'
import {Box, VisuallyHidden, VStack, Text, Link} from '@chakra-ui/react'
import {Price} from '_uiComponents'
import {useIntl} from 'react-intl'
import type {ProductType} from '_types'
import {getProductAnalyticsInfo} from '_analytics/utils/analyticsUtils'

export interface ProductInfoProps {
    product: ProductType
    customProduct: ProductType
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    styles: Record<string, any>
    selectedSizeCode: string | null
    onProductClick?: () => void
}

/**
 * @name ProductInfo
 *
 * @component
 *
 * @description
 * Renders detailed information about a product.
 *
 * @remark
 * This component shows product name, color options, size selection, and pricing details.
 * It is accessible with screen readers and supports click events for product interaction.
 *
 * @param {ProductInfoProps} props
 * @param {ProductType} props.product - The product information object.
 * @param {ProductType} props.customProduct - The customized product information object, used when available.
 * @param {Record<string, any>} props.styles - Custom styles for the component.
 * @param {string | null} props.selectedSizeCode - The code for the selected size, if any.
 * @param {() => void} [props.onProductClick] - Function to be called when the product is clicked.
 *
 * @returns {React.FC} The `ProductInfo` component.
 */
const ProductInfo: React.FC<ProductInfoProps> = ({product, customProduct, styles, selectedSizeCode, onProductClick}) => {
    const {activeVariation, productPrice, isProductSet, discountedPrice, priceMinRange, discountedPriceMinRange, productLink, productAnalyticsColorLabel} =
        getProductAnalyticsInfo(customProduct || product)
    const {formatMessage} = useIntl()

    const productName =
        !product.c_shortDescription && isProductSet
            ? formatMessage({id: 'plp.shopByLook.tileText', defaultMessage: 'Discover the look'})
            : product.c_shortDescription || activeVariation?.shortDescription

    const colorsLength = product?.c_colorsQuantity || 0
    const availableColors = formatMessage({id: 'label.tile.swatch.colors.numbers.available', defaultMessage: '{0} COLORS'}, {0: colorsLength})
    const selectedSize = selectedSizeCode ? activeVariation?.variants?.find((variant) => variant.ID === selectedSizeCode)?.size?.label : ''

    const productSetAriaLabel =
        !product.c_shortDescription && isProductSet
            ? formatMessage({id: 'aria.plus.shopbylook', defaultMessage: 'Check out the look'}) + ` ${product.id}`
            : undefined

    const productAriaLabelledBy = !isProductSet ? `sr-${product.id}-name sr-${product.id}-color sr-${product.id}-size sr-${product.id}-price` : undefined

    return (
        <>
            <VisuallyHidden>
                <Box as="span" id={`sr-${product.id}-name`}>
                    {productName}
                </Box>
                <Box as="span" id={`sr-${product.id}-color`}>
                    {productAnalyticsColorLabel}
                </Box>
                <Box as="span" id={`sr-${product.id}-size`}>
                    {selectedSize}
                </Box>
            </VisuallyHidden>
            <VStack
                as={Link}
                href={productLink}
                tabIndex={0}
                className="product-tile-link"
                {...styles.details}
                onClick={onProductClick}
                aria-label={productSetAriaLabel}
                aria-labelledby={productAriaLabelledBy}
            >
                <Text {...styles.name} {...(isProductSet ? styles.productSetTitle : {})}>
                    {productName}
                </Text>
                {!isProductSet && (
                    <>
                        <Price
                            discountedPrice={discountedPrice}
                            priceMinRange={priceMinRange}
                            discountedPriceMinRange={discountedPriceMinRange}
                            id={`sr-${product.id}-price`}
                        >
                            {productPrice}
                        </Price>
                        {colorsLength > 1 && <Text {...styles.colorsAvailability}>{availableColors}</Text>}
                    </>
                )}
            </VStack>
        </>
    )
}

export {ProductInfo}
